import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { useDismissOnEscape, useFocusTrap } from "../../lib/a11y";

const TIER_MAX = {
  compact: "max-w-[min(100vw,480px)]",
  medium: "max-w-[min(100vw,600px)]",
  wide: "max-w-[min(100vw,720px)]",
};

/**
 * Theme-agnostic right slide-over.
 * width: 100% + tiered max-width — never a fixed pixel width.
 */
export default function RightDrawer({
  open,
  onClose,
  tier = "compact",
  ariaLabel,
  children,
  testId = "right-drawer",
  closeTestId = "right-drawer-close",
  backdropTestId = "right-drawer-backdrop",
  className = "",
  panelClassName = "",
  showClose = true,
  trapFocus = true,
  hostTheme = null,
  hostThemeDark = false,
}) {
  const dialogRef = useRef(null);

  useDismissOnEscape(onClose, open);
  useFocusTrap(dialogRef, open && trapFocus);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const maxWidthClass = TIER_MAX[tier] || TIER_MAX.compact;

  const node = (
    <div
      className={twMerge(
        "fixed inset-0 z-[60] flex justify-end bg-ink/50 !m-0",
        className,
      )}
      onClick={onClose}
      role="presentation"
      data-testid={backdropTestId}
    >
      <div
        ref={dialogRef}
        className={twMerge(
          "right-drawer-in relative flex w-full flex-col bg-canvas",
          "shadow-[-24px_0_64px_rgba(0,0,0,0.18),-8px_0_24px_rgba(0,0,0,0.06)]",
          maxWidthClass,
          panelClassName,
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-testid={testId}
        data-host-theme={hostTheme || undefined}
        data-host-theme-dark={hostTheme && hostThemeDark ? "true" : undefined}
      >
        {showClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid={closeTestId}
            className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-surface/90 text-ink-secondary shadow-[0_2px_10px_rgba(0,0,0,0.1)] backdrop-blur-sm transition-colors hover:bg-surface hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
          </button>
        ) : null}
        {children}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

/** Scrollable body for drawer content under the floating close chip. */
export function RightDrawerBody({ children, className = "" }) {
  return (
    <div className={twMerge("flex-1 min-h-0 overflow-y-auto overscroll-contain", className)}>
      {children}
    </div>
  );
}
